import { useEffect, useState } from 'react';

/**
 * Breakpoints, named once so a component never re-states a pixel value.
 *
 * These are the two points where the inbox changes shape, not a generic scale:
 * below `md` there is room for one pane, below `xl` for two, above it three.
 */
export const BREAKPOINT = {
  /** Two panes become one. */
  md: 768,
  /** Two panes become three. */
  xl: 1280,
} as const;

/**
 * Subscribes to a media query.
 *
 * Reads the match during the first render rather than after an effect, so the
 * correct layout paints immediately — a mobile view that flashes the desktop
 * layout for one frame is the usual symptom of getting this wrong.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export type PaneLayout = 'single' | 'dual' | 'triple';

/** How many panes fit right now. The inbox reads this instead of guessing from width. */
export function usePaneLayout(): PaneLayout {
  const dual = useMediaQuery(`(min-width: ${BREAKPOINT.md}px)`);
  const triple = useMediaQuery(`(min-width: ${BREAKPOINT.xl}px)`);
  return triple ? 'triple' : dual ? 'dual' : 'single';
}
